// react
import React from "react";
import { useState } from "react";
import { Dropdown, DropdownButton } from "react-bootstrap";
// redux
import { useAppDispatch } from "../../../../redux/hooks";
import { itemsRemoved, INopimsBasketItemModel, batchItemsDownloaded, itemsChecked, itemsUnChecked } from "../../nopimsBasketSlice";
// kendo
import { process, State } from "@progress/kendo-data-query";
import { Grid, GridColumn, GridPageChangeEvent } from "@progress/kendo-react-grid";
// common
import * as GlobalHelpers from "../../../../common/GlobalHelpers";
import * as Forms from "../../../../common/forms/BootstrapForms";
import SimpleAlert from "../../../../common/simpleAlerts/SimpleAlertView";
import CustomGridCell from "../../../../common/kendoGrid/CustomGridCell";
import { IDownloadEntry } from "../../../../common/documentHelper";
import ConfirmationView, { DefaultButtonEnum } from "../../../../common/confirmation/ConfirmationView";
// me
import { IRootViewModel, ViewStateEnum } from "../models/models";
import BasketItemGridColumnsView from "./BasketItemGridColumnView";

interface IProps {
    vm: IRootViewModel;
    setVm: (vm: IRootViewModel) => void;
    items: INopimsBasketItemModel[];
    onExport: () => void;
    onDownloadItems: (downloadItems: IDownloadEntry[]) => void;
}

export default function BasketView(props: IProps): React.JSX.Element {
    const { vm, setVm, items } = props;
    const dispatch = useAppDispatch();

    const downloadSelectionLimit = 10;

    // grid state
    const [pageSize, setPageSize] = useState(10);
    const initialGridState = {
        sort: [{ field: "dataDescription", dir: "asc" }],
        skip: 0,
        take: pageSize,
    } as State;
    const [gridState, setGridState] = useState(initialGridState);

    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const [showDownloadConfirmation, setShowDownloadConfirmation] = useState(false);

    function onItemCheckedChanged(item: INopimsBasketItemModel) {
        if (item.isChecked) dispatch(itemsUnChecked([item]));
        else dispatch(itemsChecked([item]));
    }

    function onRemoveItems(): void {
        dispatch(itemsRemoved(items.filter((i) => i.isChecked)));
        setShowRemoveConfirmation(false);
        setVm(vm.stateChanged(ViewStateEnum.Basket));
    }
    function onDownloadItems(): void {
        const downloadItems = items
            .filter((i) => i.isChecked)
            .map((item) => {
                return { url: item.downloadUrl!, fileName: item.downloadName! };
            });

        dispatch(batchItemsDownloaded());
        props.onDownloadItems(downloadItems);

        setShowDownloadConfirmation(false);
    }

    const selectedItemsMessage =
        vm.viewState === ViewStateEnum.Download
            ? `${items.filter((i) => i.isChecked).length} items selected, total file size ${GlobalHelpers.convertBytesToString(
                  items.filter((i) => i.isChecked).reduce((sum, item) => sum + (item.fileSizeBytes ?? 0), 0)
              )}. (Maximum ${downloadSelectionLimit} items)`
            : `${items.filter((i) => i.isChecked).length} items selected.`;

    return (
        <>
            <div className="d-flex justify-content-between">
                <div className="mt-2">
                    {vm.viewState === ViewStateEnum.Basket && (
                        <>
                            {items.length > 0 ? (
                                <>
                                    Basket items with a link in the Download column can be downloaded directly. Items without this link require a Request to be submitted, and there will be a cost
                                    associated with the order.
                                </>
                            ) : (
                                <>The basket is empty.</>
                            )}
                        </>
                    )}
                    <div>
                        {vm.viewState === ViewStateEnum.Remove && <p>Select the items to remove from the basket.</p>}
                        {vm.viewState === ViewStateEnum.Request && <p>Select the items to request from the Basket, the requested items will have an associated cost.</p>}
                        {vm.viewState === ViewStateEnum.Download && <>Select the items to batch download from the basket. Items can be downloaded individually.</>}
                        {vm.viewState === ViewStateEnum.Download && items.filter((i) => !i.downloadUrl).length > 0 && <p>Some items in the basket are unable to be downloaded.</p>}
                    </div>
                </div>
                {vm.viewState === ViewStateEnum.Basket && (
                    <button
                        className="btn btn-primary mb-2 me-2 px-2 bottom"
                        type="button"
                        disabled={items.length === 0}
                        onClick={() => props.onExport()}
                        title="Click to export the NOPIMS Basket list."
                    >
                        Export Basket listing
                    </button>
                )}
            </div>
            {vm.viewState === ViewStateEnum.Request && items.filter((i) => i.isChecked && i.downloadUrl).length > 0 && (
                <SimpleAlert message="There are basket items with a downloadable link." alertType="warning" dismissable={false} />
            )}

            <Grid
                pageable={{ pageSizes: [10, 20, 50, 100], pageSizeValue: pageSize, responsive: false }}
                onPageChange={(e: GridPageChangeEvent) => {
                    setPageSize(e.page.take);
                    const updatedGridState = { ...gridState, take: e.page.take, skip: e.page.skip };
                    setGridState(updatedGridState);
                }}
                sortable
                resizable
                navigatable
                data={process(items, gridState)}
                {...gridState}
                onDataStateChange={(e) => {
                    setGridState(e.dataState);
                }}
            >
                {vm.viewState !== ViewStateEnum.Basket && vm.viewState !== ViewStateEnum.Submit && (
                    <GridColumn
                        width={32}
                        cell={(props) => (
                            <CustomGridCell gridCellProps={props}>
                                <>
                                    {props.dataItem.downloadUrl && vm.viewState === ViewStateEnum.Download && (
                                        <input
                                            type="checkbox"
                                            className={Forms.CheckInputClass}
                                            checked={props.dataItem.isChecked}
                                            disabled={items.filter((i) => i.isChecked).length >= downloadSelectionLimit && !props.dataItem.isChecked}
                                            onChange={() => onItemCheckedChanged(props.dataItem)}
                                        />
                                    )}
                                    {(vm.viewState === ViewStateEnum.Remove || vm.viewState === ViewStateEnum.Request) && (
                                        <input type="checkbox" className={Forms.CheckInputClass} checked={props.dataItem.isChecked} onChange={() => onItemCheckedChanged(props.dataItem)} />
                                    )}
                                </>
                            </CustomGridCell>
                        )}
                    />
                )}
                {BasketItemGridColumnsView()}
            </Grid>

            <div className="mt-2">{vm.viewState !== ViewStateEnum.Basket && <>{selectedItemsMessage}</>}</div>
            <div className="mt-2">
                {vm.viewState === ViewStateEnum.Basket && (
                    <>
                        <button
                            className="btn btn-primary ms-2 px-2"
                            type="button"
                            disabled={items.length === 0}
                            onClick={() => setVm(vm.stateChanged(ViewStateEnum.Request))}
                            title="Click to select items to request."
                        >
                            Progress Request
                        </button>
                        <button
                            className="btn btn-primary ms-2 px-2"
                            type="button"
                            disabled={items.length === 0}
                            onClick={() => setVm(vm.stateChanged(ViewStateEnum.Download))}
                            title="Click to select items to download."
                        >
                            Download Items
                        </button>
                        <button
                            className="btn btn-primary ms-2 px-2"
                            type="button"
                            disabled={items.length === 0}
                            onClick={() => setVm(vm.stateChanged(ViewStateEnum.Remove))}
                            title="Click to select items to remove."
                        >
                            Remove Items
                        </button>
                    </>
                )}
                {vm.viewState === ViewStateEnum.Download && (
                    <div className="d-flex">
                        <DropdownButton title="Select Items" className="btn-primary px-2">
                            <Dropdown.Item
                                as="button"
                                onClick={() => {
                                    const checkedItems = items.filter((i) => i.isChecked);

                                    // Note: Replicate sorted data to select items in display order.
                                    const sortedGridData = process(items, { sort: gridState.sort });
                                    sortedGridData.data.forEach((item) => {
                                        if (checkedItems.length < downloadSelectionLimit && !checkedItems.includes(item) && item.downloadUrl) {
                                            checkedItems.push(item);
                                            dispatch(itemsChecked([item]));
                                        }
                                    });
                                }}
                            >
                                All Items Downloadable (up to {downloadSelectionLimit} limit)
                            </Dropdown.Item>
                            <Dropdown.Item
                                as="button"
                                onClick={() => {
                                    const checkedItems = items.filter((i) => i.isChecked);

                                    const sortedGridData = process(items, { sort: gridState.sort });
                                    sortedGridData.data.forEach((item) => {
                                        if (checkedItems.length < downloadSelectionLimit && !checkedItems.includes(item) && !item.isFileDownloaded && item.downloadUrl) {
                                            checkedItems.push(item);
                                            dispatch(itemsChecked([item]));
                                        }
                                    });
                                }}
                            >
                                All Items Not Downloaded (ZIP files) (up to {downloadSelectionLimit} limit)
                            </Dropdown.Item>
                            <Dropdown.Item as="button" onClick={() => dispatch(itemsUnChecked(items))}>
                                Deselect All Items
                            </Dropdown.Item>
                        </DropdownButton>
                        <button
                            className="btn btn-primary px-2"
                            type="button"
                            disabled={items.filter((i) => i.isChecked).length === 0}
                            onClick={() => {
                                setShowDownloadConfirmation(true);
                            }}
                            title="Click to download selected items."
                        >
                            Download Selected Items
                        </button>
                        <button
                            className="btn btn-primary ms-2 px-2"
                            type="button"
                            onClick={() => {
                                dispatch(itemsUnChecked(items));
                                setVm(vm.stateChanged(ViewStateEnum.Basket));
                            }}
                            title="Click to cancel batch downloading selected items from basket."
                        >
                            Cancel
                        </button>
                    </div>
                )}
                {vm.viewState === ViewStateEnum.Remove && (
                    <div className="d-flex">
                        <DropdownButton title="Select Items" className="btn-primary px-2">
                            <Dropdown.Item
                                as="button"
                                onClick={() => {
                                    dispatch(itemsChecked(items));
                                }}
                            >
                                All Items
                            </Dropdown.Item>
                            <Dropdown.Item
                                as="button"
                                onClick={() => {
                                    const checkedItems = items.filter((i) => i.isChecked);

                                    const sortedGridData = process(items, { sort: gridState.sort });
                                    sortedGridData.data.forEach((item) => {
                                        if (!checkedItems.includes(item) && item.isFileDownloaded) {
                                            checkedItems.push(item);
                                            dispatch(itemsChecked([item]));
                                        }
                                    });
                                }}
                            >
                                All Items Downloaded (ZIP files)
                            </Dropdown.Item>
                            <Dropdown.Item as="button" onClick={() => dispatch(itemsUnChecked(items))}>
                                Deselect All Items
                            </Dropdown.Item>
                        </DropdownButton>
                        <button
                            className="btn btn-primary px-2"
                            type="button"
                            disabled={items.filter((i) => i.isChecked).length === 0}
                            onClick={() => {
                                setShowRemoveConfirmation(true);
                            }}
                            title="Click to remove selected items."
                        >
                            Remove Selected Items
                        </button>
                        <button
                            className="btn btn-primary ms-2 px-2"
                            type="button"
                            onClick={() => {
                                dispatch(itemsUnChecked(items));
                                setVm(vm.stateChanged(ViewStateEnum.Basket));
                            }}
                            title="Click to cancel removing selected items from basket."
                        >
                            Cancel
                        </button>
                    </div>
                )}
                {vm.viewState === ViewStateEnum.Request && (
                    <div className="d-flex">
                        <DropdownButton title="Select Items" className="btn-primary px-2">
                            <Dropdown.Item as="button" onClick={() => dispatch(itemsChecked(items))}>
                                All Items
                            </Dropdown.Item>
                            <Dropdown.Item as="button" onClick={() => dispatch(itemsChecked(items.filter((i) => !i.isFileDownloaded)))}>
                                All Items Not Downloaded (ZIP files)
                            </Dropdown.Item>
                            <Dropdown.Item as="button" onClick={() => dispatch(itemsChecked(items.filter((i) => !i.downloadUrl)))}>
                                All Items Unvailable for Download
                            </Dropdown.Item>
                        </DropdownButton>
                        <button
                            className="btn btn-primary px-2"
                            type="button"
                            disabled={items.filter((i) => i.isChecked).length === 0}
                            onClick={() => {
                                const checkedItems = items.filter((i) => i.isChecked);
                                dispatch(itemsChecked(checkedItems));
                                setVm(vm.requestedBasketItemsChanged(checkedItems).stateChanged(ViewStateEnum.Submit));
                            }}
                            title="Click to confirm requested items"
                        >
                            Confirm Requested Items
                        </button>
                        <button
                            className="btn btn-primary ms-2 px-2"
                            type="button"
                            onClick={() => {
                                dispatch(itemsUnChecked(items));
                                setVm(vm.stateChanged(ViewStateEnum.Basket));
                            }}
                            title="Click to cancel requesting selected items from basket."
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </div>
            {showDownloadConfirmation && (
                <ConfirmationView title="Download Items" onAccepted={() => onDownloadItems()} onClosed={() => setShowDownloadConfirmation(false)} defaultButton={DefaultButtonEnum.Cancel}>
                    <p>You have selected {items.filter((i) => i.isChecked).length} items to batch download from your basket. Do you wish to proceed?</p>
                </ConfirmationView>
            )}
            {showRemoveConfirmation && (
                <ConfirmationView title="Remove Items" onAccepted={() => onRemoveItems()} onClosed={() => setShowRemoveConfirmation(false)} defaultButton={DefaultButtonEnum.Cancel}>
                    <p>You have selected {items.filter((i) => i.isChecked).length} items to remove from your basket. Do you wish to proceed?</p>
                </ConfirmationView>
            )}
        </>
    );
}
